import { Language, PresentationType, VIEWPORTS, SLIDE_IDS } from '@constants';
import React from 'react';

const { 
    introduction, 
    yourAnalysis, 
    weCanHelp, 
    termOptions, 
    modification, 
    summary, 
    additionalInfo, 
    companyYouCanTrust,
    clientTestimonials,
    clientPortal
} = SLIDE_IDS;

export type VideoData = {
    play: boolean,
    pause: boolean
}

export type PointerPosition = {
    x: number,
    y: number
}

export type LoanCalculations = {
    APR: number,
    loanAmount: number,
    loanTerm: number,
    monthlyPayment: number,
    totalRepayment: number,
    estTotalSavings: number
}

export type SharedPresentationState = {
    presentationId: number | null,
    slideId: number,
    lastSlideId: number | null,
    pdfPageNumber: number,
    selectedTerm: {
        [termOptions]: number,
        [summary]: number
    },
    stepsReviewed: {
        [weCanHelp]: string[],
        [modification]: string[],
        [additionalInfo]: string[],
        [companyYouCanTrust]: string[],
        [clientTestimonials]: string[],
        [clientPortal]: string[]
    },
    infoItem: string,
    loanCalculations: LoanCalculations,
    viewport: VIEWPORTS.desktop,
    slideWidth: number,
    availableTerms: number[],
    presentationEnded: boolean,
    showPointer: boolean,
    language: Language
}

export type SalesRepPresentationState = SharedPresentationState & {
    clientConnected: boolean,
    selectedSection: {
        [introduction]: number,
        [yourAnalysis]: number,
        [weCanHelp]: number,
        [termOptions]: number,
        [modification]: number,
        [summary]: number,
        [additionalInfo]: number
    }
}

export type ClientPresentationState = SharedPresentationState & {
    repConnected: boolean,
    presentationType: PresentationType,
    pointerPosition: PointerPosition,
    videoControls: VideoData,
    scrollTop: {
        [elementId: string]: number
    },
    scrollLeft: {
        [elementId: string]: number
    }
}

export enum ActionTypes {
    SHOW_POINTER = 'SHOW POINTER',
    UPDATE_POINTER_POSITION = 'UPDATE_POINTER_POSITION',
    UPDATE_VIDEO_DATA = 'UPDATE_VIDEO_DATA',
    SET_SLIDE_ID = 'SET_SLIDE_ID',
    SET_LAST_SLIDE_ID = 'SET_LAST_SLIDE_ID',
    UPDATE_PDF_PAGE = 'UPDATE_PDF_PAGE',
    SELECT_TERM = 'SELECT_TERM',
    END_PRESENTATION = 'END_PRESENTATION',
    SET_VIEWPORT = 'SET_VIEWPORT',
    SELECT_SECTION = 'SELECT_SECTION',
    SET_PRESENTATION_TYPE = 'SET_PRESENTATION_TYPE',
    SET_STEP_REVIEWED = 'SET_STEP_REVIEWED',
    SELECT_INFO_ITEM = 'SELECT_INFO_ITEM',
    SET_LOAN_INPUT = 'SET_LOAN_INPUT',
    SET_LOAN_CALCULATIONS = 'SET_LOAN_CALCULATIONS',
    SET_CLIENT_CONNECTED = 'SET_CLIENT_CONNECTED',
    SET_REP_CONNECTED = 'SET_REP_CONNECTED',
    SET_SLIDE_WIDTH = 'SET_SLIDE_WIDTH',
    SET_AVAILABLE_TERMS = 'SET_AVAILABLE_TERMS',
    SET_SCROLL_TOP = 'SET_SCROLL_TOP',
    SET_SCROLL_LEFT = 'SET_SCROLL_LEFT',
    SET_LANGUAGE = 'SET_LANGUAGE',
    SET_PRESENTATION_ID = 'SET_PRESENTATION_ID'
}

export type SetShowPointerAction = {
    type: typeof ActionTypes.SHOW_POINTER,
    showPointer: boolean
}

export type SetSlideIdAction = {
    type: typeof ActionTypes.SET_SLIDE_ID,
    slideId: number
}

export type setLastSlideIdAction = {
    type: typeof ActionTypes.SET_LAST_SLIDE_ID,
    lastSlideId: number
}

export type SetPdfPageAction = {
    type: typeof ActionTypes.UPDATE_PDF_PAGE,
    page: number
}

export type SetPresentationIdAction = {
    type: typeof ActionTypes.SET_PRESENTATION_ID,
    presentationId: number
}

export type SetLanguageAction = {
    type: typeof ActionTypes.SET_LANGUAGE,
    language: Language
}

export type UpdateVideoDataAction = {
    type: typeof ActionTypes.UPDATE_VIDEO_DATA,
    videoData: VideoData
}

export type UpdatePointerPositionAction = {
    type: typeof ActionTypes.UPDATE_POINTER_POSITION,
    position: PointerPosition
}

export type SelectTermAction = {
    type: typeof ActionTypes.SELECT_TERM,
    term: number,
    slideId: number
}

export type SetPresentationEndedAction = {
    type: typeof ActionTypes.END_PRESENTATION,
    presentationEnded: boolean
}

export type SetViewportAction = {
    type: typeof ActionTypes.SET_VIEWPORT,
    viewport: VIEWPORTS
}

export type SetPresentationTypeAction = {
    type: typeof ActionTypes.SET_PRESENTATION_TYPE,
    presentationType: PresentationType
}

export type SelectSlideSectionAction = {
    type: typeof ActionTypes.SELECT_SECTION,
    id: number,
    section: number
}

export type setStepsReviewedAction = {
    type: typeof ActionTypes.SET_STEP_REVIEWED,
    id: number,
    step: string
}

export type SelectInfoItemAction = {
    type: typeof ActionTypes.SELECT_INFO_ITEM,
    item: string
}

export type SetClientConnectedAction = {
    type: typeof ActionTypes.SET_CLIENT_CONNECTED,
    connected: boolean
}

export type SetRepConnectedAction = {
    type: typeof ActionTypes.SET_REP_CONNECTED,
    connected: boolean
}

export type SetSlideWidthAction = {
    type: typeof ActionTypes.SET_SLIDE_WIDTH,
    width: number
}

export type SetLoanInputAction = {
    type: typeof ActionTypes.SET_LOAN_INPUT,
    name: string,
    value: string
}

export type setAvailableTermsAction = {
    type: typeof ActionTypes.SET_AVAILABLE_TERMS,
    terms: number[]
}

export type SetScrollTopAction = {
    type: typeof ActionTypes.SET_SCROLL_TOP,
    elementId: string,
    top: number
}

export type SetScrollLeftAction = {
    type: typeof ActionTypes.SET_SCROLL_LEFT,
    elementId: string,
    left: number
}

export type SetLoanCalculationsAction = {
    type: typeof ActionTypes.SET_LOAN_CALCULATIONS,
    calculations: LoanCalculations
}

export type PresentationActionTypes = 
    SetShowPointerAction | 
    UpdatePointerPositionAction |
    UpdateVideoDataAction |
    SetSlideIdAction | 
    SetPdfPageAction |
    SelectTermAction | 
    SetPresentationEndedAction |
    SetViewportAction |
    SetPresentationTypeAction |
    SelectSlideSectionAction |
    setStepsReviewedAction |
    SelectInfoItemAction |
    SetLoanInputAction |
    SetLoanCalculationsAction |
    SetClientConnectedAction |
    SetRepConnectedAction |
    SetSlideWidthAction |
    setAvailableTermsAction |
    SetScrollTopAction |
    SetScrollLeftAction |
    SetLanguageAction |
    SetPresentationIdAction;

export type PresentationDispatch = React.Dispatch<PresentationActionTypes>;